import { useEffect, useMemo, useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import {
  uploadCampaignReportRequest,
  uploadComparisonReportRequest,
  uploadHessReports,
  uploadLinearReports,
  uploadStreamingReports,
} from "../api/uploadApi";
import { saveReport } from "../utils/reportManager";
import type {
  CampaignReportResponse,
  CampaignReportType,
  ComparisonReportResponse,
  HessReportResponse,
  LinearReportResponse,
  SocialCampaignReportResponse,
  StreamingReportResponse,
} from "../types";
import type {
  ChannelUploadDescriptor,
  RetailerOption,
} from "../config/retailerConfig";
import {
  DEFAULT_UPLOAD_ENDPOINT,
  HESS_UPLOAD_ENDPOINT,
  LINEAR_UPLOAD_ENDPOINT,
  RETAILERS,
  STREAMING_UPLOAD_ENDPOINT,
  getUploadDescriptorForChannelFrom,
  getUploadDescriptorFromRetailersByEndpoint,
} from "../config/retailerConfig";
import type { UploadFormData } from "../pages/uploadFormSchema";

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

type UploadResponse =
  | CampaignReportResponse
  | ComparisonReportResponse
  | SocialCampaignReportResponse
  | LinearReportResponse
  | StreamingReportResponse
  | HessReportResponse;

type UseUploadPageWorkflowArgs = {
  navigate: NavigateFunction;
  retailers?: RetailerOption[];
  selectedChannel?: string;
  selectedRetailer: string;
};

type DescriptorUploadArgs = {
  descriptor: ChannelUploadDescriptor;
  formData: UploadFormData;
  selectedFile: File | null;
  selectedFiles: File[];
};

function describeCountConstraint(descriptor: ChannelUploadDescriptor): string {
  if (descriptor.fileCount !== null) {
    return descriptor.fileCount === 1
      ? "1 file"
      : `Exactly ${descriptor.fileCount} files`;
  }

  if (descriptor.minFileCount === descriptor.maxFileCount) {
    return descriptor.maxFileCount === 1
      ? "1 file"
      : `Exactly ${descriptor.maxFileCount} files`;
  }

  return `${descriptor.minFileCount}-${descriptor.maxFileCount} files`;
}

function describeFormats(descriptor: ChannelUploadDescriptor): string {
  return descriptor.fileFormats.join(", ");
}

async function uploadReportForDescriptor({
  descriptor,
  formData,
  selectedFile,
  selectedFiles,
}: DescriptorUploadArgs): Promise<UploadResponse> {
  if (descriptor.uploadEndpoint === LINEAR_UPLOAD_ENDPOINT) {
    return uploadLinearReports(selectedFiles);
  }

  if (descriptor.uploadEndpoint === STREAMING_UPLOAD_ENDPOINT) {
    return uploadStreamingReports(selectedFiles);
  }

  if (descriptor.uploadEndpoint === HESS_UPLOAD_ENDPOINT) {
    return uploadHessReports(selectedFiles);
  }

  if (!selectedFile) {
    throw new Error("Please select a file to upload");
  }

  return uploadCampaignReportRequest({
    file: selectedFile,
    reportType: descriptor.reportType as CampaignReportType | "social",
    retailer: formData.retailer,
    client: formData.client,
    channel: formData.channel,
    product: formData.product,
  });
}

export function useUploadPageWorkflow({
  navigate,
  retailers = RETAILERS,
  selectedChannel,
  selectedRetailer,
}: UseUploadPageWorkflowArgs) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isLinearMode, setIsLinearMode] = useState(false);
  const [isComparisonMode, setIsComparisonMode] = useState(false);
  const [comparisonCurrentFile, setComparisonCurrentFile] = useState<File | null>(
    null,
  );
  const [comparisonPriorFile, setComparisonPriorFile] = useState<File | null>(
    null,
  );
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const selectedDescriptor = useMemo(() => {
    const baseDescriptor =
      getUploadDescriptorForChannelFrom(
        retailers,
        selectedRetailer,
        selectedChannel || "",
      ) ||
      getUploadDescriptorFromRetailersByEndpoint(retailers, DEFAULT_UPLOAD_ENDPOINT);

    if (!baseDescriptor) {
      throw new Error("No upload descriptor configured");
    }

    if (isLinearMode) {
      return (
        getUploadDescriptorFromRetailersByEndpoint(
          retailers,
          LINEAR_UPLOAD_ENDPOINT,
        ) ||
        baseDescriptor
      );
    }

    return baseDescriptor;
  }, [retailers, selectedRetailer, selectedChannel, isLinearMode]);

  const isStreamingMode = selectedDescriptor.uploadMode === "streaming";
  const isHessMode = selectedDescriptor.uploadMode === "hess";
  const isMultiFileMode =
    selectedDescriptor.uploadMode !== "campaign" ||
    selectedDescriptor.maxFileCount > 1;

  const hasValidFileSelection = isComparisonMode
    ? Boolean(comparisonCurrentFile && comparisonPriorFile)
    : isMultiFileMode
      ? selectedFiles.length >= selectedDescriptor.minFileCount &&
        selectedFiles.length <= selectedDescriptor.maxFileCount
      : Boolean(selectedFile);

  useEffect(() => {
    setSelectedFile(null);
    setSelectedFiles([]);
    setUploadError(null);
  }, [selectedChannel]);

  const resetSelection = () => {
    setSelectedFile(null);
    setSelectedFiles([]);
    setUploadError(null);
    setUploadProgress(0);
  };

  const activateStandardMode = () => {
    setIsLinearMode(false);
    setIsComparisonMode(false);
    resetSelection();
  };

  const activateLinearMode = () => {
    setIsLinearMode(true);
    setIsComparisonMode(false);
    resetSelection();
  };

  const activateComparisonMode = () => {
    setIsComparisonMode(true);
    setIsLinearMode(false);
    resetSelection();
    setComparisonCurrentFile(null);
    setComparisonPriorFile(null);
  };

  const setComparisonFile = (slot: "current" | "prior", file: File | null) => {
    if (slot === "current") {
      setComparisonCurrentFile(file);
    } else {
      setComparisonPriorFile(file);
    }
    setUploadError(null);
  };

  const validateAndSetFile = (file: File) => {
    const extension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

    if (!selectedDescriptor.fileFormats.includes(extension)) {
      const allowedFormats = describeFormats(selectedDescriptor);
      const modeLabel = selectedDescriptor.name;
      setUploadError(
        `Invalid file format. ${modeLabel} expects ${allowedFormats} files.`,
      );
      setSelectedFile(null);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setUploadError("File size must be less than 50MB");
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setUploadError(null);
  };

  const validateAndSetFiles = (files: File[]) => {
    if (selectedDescriptor.minFileCount > 1 && files.length < selectedDescriptor.minFileCount) {
      setUploadError(
        `Please provide ${describeCountConstraint(selectedDescriptor)}. Current: ${files.length}`,
      );
      setSelectedFiles([]);
      return;
    }

    if (files.length > selectedDescriptor.maxFileCount) {
      setUploadError(
        `Maximum ${selectedDescriptor.maxFileCount} files allowed for ${selectedDescriptor.name}`,
      );
      setSelectedFiles([]);
      return;
    }

    if (
      selectedDescriptor.fileCount !== null &&
      files.length !== selectedDescriptor.fileCount
    ) {
      setUploadError(
        `Please upload ${describeCountConstraint(selectedDescriptor)} for ${selectedDescriptor.name}`,
      );
      setSelectedFiles([]);
      return;
    }

    const invalidFiles = files.filter((file) => {
      const extension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
      return !selectedDescriptor.fileFormats.includes(extension);
    });

    if (invalidFiles.length > 0) {
      setUploadError(
        `All files must be ${describeFormats(selectedDescriptor)} format files: ${invalidFiles.map((file) => file.name).join(", ")}`,
      );
      setSelectedFiles([]);
      return;
    }

    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE_BYTES);
    if (oversizedFiles.length > 0) {
      setUploadError(
        `File size must be less than 50MB: ${oversizedFiles.map((file) => file.name).join(", ")}`,
      );
      setSelectedFiles([]);
      return;
    }

    setSelectedFiles(files);
    setUploadError(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    if (isMultiFileMode) {
      validateAndSetFiles(Array.from(files));
      return;
    }

    validateAndSetFile(files[0]);
  };

  const handleDrag = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.type === "dragenter" || event.type === "dragover") {
      setDragActive(true);
    } else if (event.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    if (!event.dataTransfer.files || event.dataTransfer.files.length === 0) {
      return;
    }

    if (isMultiFileMode) {
      validateAndSetFiles(Array.from(event.dataTransfer.files));
      return;
    }

    validateAndSetFile(event.dataTransfer.files[0]);
  };

  const onSubmit = async (formData: UploadFormData) => {
    if (isComparisonMode) {
      if (!comparisonCurrentFile || !comparisonPriorFile) {
        setUploadError("Please select both a current period and prior period file");
        return;
      }
    } else if (isMultiFileMode) {
      if (selectedDescriptor.fileCount !== null) {
        if (selectedFiles.length === 0) {
          setUploadError(
            `Please select ${describeCountConstraint(selectedDescriptor)} for ${selectedDescriptor.name}`,
          );
          return;
        }

        if (selectedFiles.length < selectedDescriptor.minFileCount) {
          setUploadError(
            `Please provide at least ${selectedDescriptor.minFileCount} files for ${selectedDescriptor.name}`,
          );
          return;
        }
      }

      if (selectedFiles.length === 0) {
        setUploadError(`Please upload files for ${selectedDescriptor.name}`);
        return;
      }
    } else if (!selectedFile) {
      setUploadError("Please select a file to upload");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    let progressInterval: ReturnType<typeof setInterval> | undefined;

    try {
      progressInterval = setInterval(() => {
        setUploadProgress((current) => Math.min(current + 10, 90));
      }, 200);

      let response: UploadResponse;

      if (isComparisonMode) {
        response = await uploadComparisonReportRequest({
          currentFile: comparisonCurrentFile!,
          priorFile: comparisonPriorFile!,
          reportType: "search",
          retailer: formData.retailer,
          client: formData.client,
          channel: formData.channel,
          product: formData.product,
        });
      } else {
        response = await uploadReportForDescriptor({
          descriptor: selectedDescriptor,
          formData,
          selectedFile,
          selectedFiles,
        });
      }

      setUploadProgress(100);

      const savedReport = saveReport(response, {
        retailer: formData.retailer,
        client: formData.client,
        channel: formData.channel,
        product: formData.product || "all",
      });

      setTimeout(() => {
        navigate(`/upload/results?id=${savedReport.id}`);
      }, 500);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Upload failed";
      setUploadError(errorMessage);
    } finally {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      setIsUploading(false);
    }
  };

  return {
    activateComparisonMode,
    activateLinearMode,
    activateStandardMode,
    comparisonCurrentFile,
    comparisonPriorFile,
    comparisonModeDescriptor: selectedDescriptor,
    dragActive,
    handleDrag,
    handleDrop,
    handleFileSelect,
    isComparisonMode,
    isHessMode,
    isLinearMode,
    isStreamingMode,
    isUploading,
    onSubmit,
    selectedDescriptor,
    selectedFile,
    selectedFiles,
    setComparisonFile,
    uploadError,
    uploadProgress,
    handleReset: resetSelection,
    hasValidFileSelection,
  };
}
